import Icon from "@components/Icon";
import StyledIconMapper from "@components/IconMapper";
import Image from "next/image";
import {
	desktopLMediaQuery,
	desktopMediaQuery,
	getColours,
	laptopMediaQuery,
	mobileMediaQuery,
	tabletMediaQuery,
} from "@helpers";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Link from "next/link"
import Menu from "./Menu";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${({ showMenuModal }) => (showMenuModal ? "flex" : "none")};
  flex-direction: column;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Content = styled.div`
  width: 100%;
  z-index: 10000;
  align-self: flex-end;
  height: 100vh;
  border-top-left-radius: 0px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.25);
  animation: openMenu 0.3s ease-in;
  transform-origin: 100% 50%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  @keyframes openMenu {
    0% {
      transform: translateY(100%);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  background-color: #FFFFFF;

  ${desktopMediaQuery} {
    width: 49%;
    border-top-left-radius: 100px;
  }

  ${desktopLMediaQuery} {
    width: 49%;
    border-top-left-radius: 100px;
  }
`;

const MenuBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 66px;
  padding: 0px;
  position: relative;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 0px;
  padding-top: 11.15px;
  ${mobileMediaQuery} {
    padding-left: 13px;
    padding-top: 11.15px;
  }
`;

const LogoImage = styled(Image)`
  cursor: pointer;
`;

const Nav = styled.div`
  display: flex;
  justify-content: flex-end;
  z-index: 10001;
  align-items: center;
  width: 100%;
  height: 66px;
  padding: 0px;
  position: absolute;
  top: 0;
  right: 0;
`;

const CloseIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 25px;
  margin-top: 17px;
  ${mobileMediaQuery} {
    margin-right: 25px;
    margin-top: 17px;
  }
`;

const NavigationTabsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 0px 0px 0px;
  gap: 32px;
  flex: 1;
  box-sizing: border-box;
  overflow-y: auto;
  ${mobileMediaQuery} {
    padding: 30px 0px 0px 0px;
    gap: 0px;
  }
  ${tabletMediaQuery} {
    padding: 30px 0px 0px 0px;
    gap: 0px;
  }
`;

const MenuContentWrapper = styled.div`
  padding: 0px 25px;
  ${mobileMediaQuery} {
    padding: 0px 25px 0px 36px;
  }
  ${tabletMediaQuery} {
    padding: 0px 25px 0px 36px;
  }
`;

const MenuItemsContainer = styled.ul`
  display: flex;
  flex-direction: column;
`;

const MenuItemWrapper = styled.li`
  display: flex;
  flex-direction: column;
  margin: 0;
  margin-bottom: ${({ hasSubNav, isExpanded, isLastItem }) => {
		if (isLastItem) return "0px";
		if (hasSubNav && isExpanded) return "32px";
		return "24px";
	}};
`;

const MenuRow = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  height: 32px;
  user-select: none;
  position: relative;
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 32px;
`;

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  position: absolute;
  right: 0;
  top: 0;
  pointer-events: none;
`;

const SubMenuContainer = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  margin-left: 0px;
  padding-left: 10px;
  gap: 24px;
  position: relative;
  
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(18, 18, 82, 0.1);
  }
`;
const SubMenuLinkContainer = styled.li`
	margin: 0;
	list-style: none;
`
const SubMenuLink = styled(Link)`
  font-size: 16px;
  font-weight: 400;
  font-family: var(--euclid-regular);
  line-height: 1.375em;
  color: #121252;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 50px;
  gap: 15px;
  padding: 25px;
  background-color: rgba(255, 136, 112, 0.15);
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0px;
  ${mobileMediaQuery} {
    padding: 25px;
    margin-bottom: 0px;
  }
  ${tabletMediaQuery} {
    padding: 25px;
    margin-bottom: 0px;
  }
`;

const CTAButton = styled.button`
  font-size: 16px;
  font-weight: 500;
  font-family: var(--euclid-medium);
  line-height: 1.375em;
  background-color: ${(props) => {
		const bgColor = getColours(props.backgroundColor);
		// Buy now button: Midnight Blue background, White text
		if (bgColor === "#121252" || bgColor === "#1A1A52") {
			return "#121252";
		}
		// Activate SIM button: White background, Midnight Blue text
		return "#FFFFFF";
	}};
  color: ${(props) => {
		const bgColor = getColours(props.backgroundColor);
		if (bgColor === "#121252" || bgColor === "#1A1A52") {
			return "#FFFFFF";
		}
		return "#121252";
	}} !important;
  border: ${(props) => {
		const bgColor = getColours(props.backgroundColor);
		if (bgColor === "#121252" || bgColor === "#1A1A52") {
			return "1px solid #121252";
		}
		return "1px solid #121252";
	}};
  border-radius: 10px;
  padding: 0px 32px;
  height: 48px;
  cursor: pointer;
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${mobileMediaQuery} {
    font-size: 14px;
  }

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const NavigationMenuModal = ({
	showMenuModal,
	setShowMenuModal,
	navigationList,
	navigationBackgroundColor,
	navigationcontentcolor,
	buttonText,
	buttonColor,
	logo,
}) => {
	const pathname = usePathname();
	const contentRef = useRef(null);
	const [expandedMenus, setExpandedMenus] = useState(new Set());

	const closeNavigationMenuModal = () => {
		setShowMenuModal(false);
		setExpandedMenus(new Set());
		if (typeof document !== "undefined") {
			const burgerMenuButton = document.getElementById("Menu");
			burgerMenuButton?.focus();
		}
	};

	const handleCloseKeyDown = (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			closeNavigationMenuModal();
		}
	};

	const toggleMenu = (menuName) => {
		setExpandedMenus((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(menuName)) {
				newSet.delete(menuName);
			} else {
				newSet.add(menuName);
			}
			return newSet;
		});
	};

	// Reset expanded menus when modal closes
	useEffect(() => {
		if (!showMenuModal) {
			setExpandedMenus(new Set());
		}
	}, [showMenuModal]);

	// Prevent body scroll when menu is open
	useEffect(() => {
		if (showMenuModal) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [showMenuModal]);

	// Focus management: Move focus to the first focusable element when menu opens
	useEffect(() => {
		if (showMenuModal) {
			// Small delay to ensure the menu is rendered and visible
			const timeoutId = setTimeout(() => {
				if (contentRef.current) {
					// Find first focusable element in the content
					const firstFocusable = contentRef.current.querySelector(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					);
					if (firstFocusable) {
						firstFocusable.focus();
					}
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [showMenuModal]);

	// Focus trapping: Keep focus within the menu when it's open
	useEffect(() => {
		if (!showMenuModal || !contentRef.current) return;

		const handleKeyDown = (e) => {
			if (e.key !== "Tab") return;

			const focusableElements = contentRef.current.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);

			if (focusableElements.length === 0) return;

			const firstFocusable = focusableElements[0];
			const lastFocusable = focusableElements[focusableElements.length - 1];

			// Check if focus is within the menu content
			const isFocusInMenu = contentRef.current.contains(document.activeElement);

			if (!isFocusInMenu) {
				// If focus is outside, move it to the first element
				e.preventDefault();
				firstFocusable.focus();
				return;
			}

			if (e.shiftKey) {
				// Shift + Tab
				if (document.activeElement === firstFocusable) {
					e.preventDefault();
					lastFocusable.focus();
				}
			} else {
				// Tab
				if (document.activeElement === lastFocusable) {
					e.preventDefault();
					firstFocusable.focus();
				}
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [showMenuModal]);

	const renderSubMenu = (navInfo, isExpanded) => {
		if (!isExpanded || !navInfo.subNav) return null;

		return (
			<SubMenuContainer>
				{navInfo.subNav.map((subNavInfo, index) => {
					const activeStyle = subNavInfo.link === pathname;
					return (
						<SubMenuLinkContainer>
							<SubMenuLink
								key={subNavInfo.order}
								href={subNavInfo.link}
								navigationcontentcolor={navigationcontentcolor}
								onClick={closeNavigationMenuModal}
							>
								{subNavInfo.name}
							</SubMenuLink>
						</SubMenuLinkContainer>
					);
				})}
			</SubMenuContainer>
		);
	};

	const renderNavigationTab = () => {
		if (!navigationList) return null;

		return (
			<MenuItemsContainer>
				{navigationList.map((navInfo, index) => {
					const activeStyle = navInfo.link === pathname;
					const hasSubNav = navInfo.subNav && navInfo.subNav.length > 0;
					const isExpanded = expandedMenus.has(navInfo.name);
					const isLastItem = index === navigationList.length - 1;

					return (
						<MenuItemWrapper
							key={navInfo.name}
							hasSubNav={hasSubNav}
							isExpanded={isExpanded}
							isLastItem={isLastItem}
						>
							<MenuRow
								onClick={() => {
									if (hasSubNav) {
										toggleMenu(navInfo.name);
									} else {
										closeNavigationMenuModal();
										setTimeout(() => {
											window.location.href = navInfo.link;
										}, 100);
									}
								}}
								// biome-ignore lint/a11y/useSemanticElements: <explanation>
								role={hasSubNav ? "button" : "none"}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										if (hasSubNav) {
											toggleMenu(navInfo.name);
										} else {
											closeNavigationMenuModal();
											setTimeout(() => {
												window.location.href = navInfo.link;
											}, 100);
										}
									}
								}}
							>
								<MenuContainer>
									<Menu
										navInfo={navInfo}
										data-gtm-cta-position="Navigation"
										data-gtm-cta-text={navInfo.name}
										navigationcontentcolor={navigationcontentcolor}
										activeStyle={activeStyle}
										navigationBackgroundColor={navigationBackgroundColor}
										closeNavigationMenuModal={closeNavigationMenuModal}
										hasSubNav={hasSubNav}
										isExpanded={isExpanded}
									/>
								</MenuContainer>
								{hasSubNav && (
									<ChevronContainer>
										<Icon
											iconName={isExpanded ? "chevron-up" : "chevron-down"}
											iconSizes={{
												iconMobile: 32,
												iconTablet: 32,
												iconLaptop: 32,
												iconDesktop: 32,
												iconDesktopLarge: 32,
											}}
											alt={isExpanded ? "Collapse" : "Expand"}
											style={{ pointerEvents: "none" }}
										/>
									</ChevronContainer>
								)}
							</MenuRow>
							{renderSubMenu(navInfo, isExpanded)}
						</MenuItemWrapper>
					);
				})}
			</MenuItemsContainer>
		);
	};

	const handleBuyNowClick = () => {
		closeNavigationMenuModal();
		setTimeout(() => {
			const currentParams = window.location.search;
			window.location.href = currentParams ? `/buy${currentParams}` : "/buy";
		}, 100);
	};

	const handleActivateSimClick = () => {
		closeNavigationMenuModal();
		setTimeout(() => {
			window.location.href = "/activate/retail";
		}, 100);
	};

	const loaderProp = ({ src }) => {
		return src;
	};

	const LogoArray = logo?.fields;

	return (
		<Container
			className="full-size-modal"
			showMenuModal={showMenuModal}
			onClick={closeNavigationMenuModal}
		>
			<Content
				ref={contentRef}
				onClick={(e) => e.stopPropagation()}
				navigationBackgroundColor={navigationBackgroundColor}
			>
				<MenuBar>
					{logo && (
						<LogoContainer>
							<a href="/" aria-label="Click on felix Logo to go to homepage">
								<LogoImage
									className="clickable"
									src="/images/felixmobile_Logo_RGB_Secondary_Navy_withPeach.png"
									alt={LogoArray?.description}
									priority
									width={207}
									height={28}
									loader={loaderProp}
								/>
							</a>
						</LogoContainer>
					)}
					<Nav>
						<CloseIconWrapper>
							<StyledIconMapper
								iconRef="close"
								color="#1E1950"
								sizeMobile={32}
								sizeLaptop={32}
								sizeDesktop={32}
								onClick={closeNavigationMenuModal}
								onKeyDown={handleCloseKeyDown}
								tabIndex={0}
								role="button"
								aria-label="Close menu"
								style={{ cursor: "pointer" }}
							/>
						</CloseIconWrapper>
					</Nav>
				</MenuBar>
				<NavigationTabsSection>
					<MenuContentWrapper>{renderNavigationTab()}</MenuContentWrapper>
					<CTASection>
						{buttonText && (
							<CTAButton
								backgroundColor={buttonColor || "Midnight Blue"}
								onClick={handleBuyNowClick}
							>
								{buttonText}
							</CTAButton>
						)}
						<CTAButton backgroundColor="White" onClick={handleActivateSimClick}>
							Activate SIM
						</CTAButton>
					</CTASection>
				</NavigationTabsSection>
			</Content>
		</Container>
	);
};

export default NavigationMenuModal;
